import {IntlService} from "@progress/kendo-vue-intl";
const kendo = new IntlService('en-US');

export default class DieRank {
    static pattern: RegExp = /d([0-9]+)([\+\-])?([0-9]+)?/;

    private dieStep: string | null = null;
    private dieSides: number | null = null;
    private dieModifier: number | null = null;

    constructor(value: string) {
        this.parse(value);
    }

    private parse(value: string): void {
        if (DieRank.pattern.test(value)) {
            const parsedValue = DieRank.pattern.exec(value);
            if (parsedValue) {
                this.dieSides = parseInt(parsedValue[1]);
                this.dieStep = 'd'+ this.dieSides;

                let modifier: string | number | null = parsedValue[2] + parsedValue[3];

                if (modifier) {
                    modifier = modifier.replace(" +", "");
                }

                this.dieModifier = parseInt(modifier);
                if (isNaN(this.dieModifier)) {
                    this.dieModifier = null;
                }
            }
        }
    }

    public isParsed(): boolean {
        return (typeof this.dieStep === 'string');
    }

    public getRankAsString(): string | null {
        return this.dieStep;
    }

    public getDieCode(): string | null {
        if ((this.dieModifier ?? 0) !== 0) {
            return kendo.format('{0}+{1}', [this.dieStep, this.dieModifier]);
        }

        return this.getRankAsString()
    }

    public getDieSides(): number | null {
        return this.dieSides;
    }

    // Step index on the die ladder (d4 = 0, d6 = 1, d8 = 2, d10 = 3, d12 = 4), so two ranks can be
    // compared as a signed number of steps.
    //
    // Built on getDieSides(), NOT getValue(): getValue() folds in the flat modifier, so 'd6+2'
    // and 'd10' would read as the same step and a purchasing-power baseline comparison would be
    // silently wrong.
    public getSteps(): number {
        return Math.max(0, ((this.getDieSides() ?? 4) - 4) / 2);
    }

    public getDieModifier(): number | null {
        return this.dieModifier;
    }

    public getValue(): number {
        return (this.dieSides ?? 0) + (this.dieModifier ?? 0);
    }

    public getDerivedStatValue(): number | null {
        return 2 + (Math.floor(this.getValue() / 2));
    }

    public addSteps(steps: number = 1): DieRank {
        if (this.isParsed() && steps !== 0) {
            let newSides: number = (this.dieSides ?? 4) + (steps * 2);
            let newModifier: number | null = this.dieModifier ?? 0;

            if ((this.dieSides === 4) && (this.dieModifier === -2) && (steps === 1)) {
                newSides = 4;
                newModifier = 0;
            }
            else if ((this.dieSides === 4) && (this.dieModifier === -2)) {
                newSides = this.dieSides + ((steps - 1) * 2);
                newModifier = 0;
            }

            // console.debug("DieRank | ", newSides, newModifier);

            if (newSides < 4) {
                newSides = 4;
                newModifier = (newModifier ?? 0) - 2;
            }
            else if (newSides > 12) {
                newSides = 12;
                newModifier = (newModifier ?? 0) + (Math.floor(((this.dieSides ?? 4) - newSides) / 2) + steps)
            }

            // console.debug("DieRank | ", newSides, newModifier);

            if (newModifier === 0) {
                newModifier = null;
            }

            // console.debug("DieRank | ", newSides, newModifier);

            this.dieSides = newSides;
            this.dieModifier = newModifier;

            this.dieStep = kendo.format("d{0:0}", this.dieSides);
            
            // console.debug(this);
        }

        return this;
    }

    public subtractSteps(steps: number = 1): DieRank {
        if (this.isParsed() && steps !== 0) {
            let newSides: number = this.dieSides ?? 4;
            let newModifier: number | null = null;
            /**
             * It's mostly a problem when you start dealing with d12s. Sometimes the modifier is a
             * flat value that is supposed to be applied and sometimes its because the character is
             * legendary and took one of those Edges. So, sometimes you want to step down the entire
             * die sides and sometimes you want to step it down first by the modifier and then by
             * the die sides.
             */
            if ((this.dieSides === 12) && (this.dieModifier ?? 0) > 0) {
                newModifier = (this.dieModifier ?? 0) - steps;
            }
            else {
                newSides = (this.dieSides ?? 4) - (steps * 2);
            }

            // console.debug("DieRank | ", newSides, newModifier);

            if ((newModifier ?? 0) < 0) {
                newSides = (this.dieSides ?? 4) - (Math.abs((newModifier ?? 0)) * 2);
                newModifier = 0;
            }

            if (newSides < 4) {
                newSides = 4;
                newModifier = (newModifier ?? 0) - 2;
            }

            // console.debug("DieRank | ", newSides, newModifier);

            if (newModifier === 0) {
                newModifier = null;
            }

            // console.debug("DieRank | ", newSides, newModifier);

            this.dieSides = newSides;
            this.dieModifier = newModifier;

            this.dieStep = kendo.format("d{0:0}", this.dieSides);

            // console.debug(this);
        }

        return this;
    }

    public addModifier(value: number = 1): DieRank {
        if (this.isParsed() && value !== 0) {
            let newModifier: number | null = (this.dieModifier ?? 0) + value;

            if (newModifier === 0) {
                newModifier = null;
            }

            this.dieModifier = newModifier;
        }

        return this;
    }

    public subtractModifier(value: number = 1): DieRank {
        if (this.isParsed() && value !== 0) {
            let newModifier: number | null = (this.dieModifier ?? 0) - Math.abs(value);

            if (newModifier === 0) {
                newModifier = null;
            }

            this.dieModifier = newModifier;
        }

        return this;
    }
}
